import React from 'react';
import i18n from '@/common/agent/i18n';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { PATH } from '@/main/controller/MainController';
import Text from '@/common/view/Text';
import Icon from '@/common/view/icons';
import { useAuth } from '@/auth/signin/datacontroller/AuthContext';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from '@/common/view/dropdown';
import { Whisper, Popover } from 'rsuite';
import MainSetting from '@/main/model/MainSetting';
import colors from '@/common/view/colors';
import SubMenuItem from './SubMenuItem';
import { useDelayedVisibility } from './useDelayedVisibility';

const ProfileBox = styled.div<{ $active?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 68px;
  padding: 16px;
  color: ${({ $active }) => ($active ? colors.blue50 : colors.gray40)};
`;

const TextBox = styled.div`
  flex: 1;
  margin: 0 6.4px;
  span {
    font-size: 11.2px;
  }
`;
const IconBox = styled.div`
  cursor: pointer;
`;

const PopoverMenu = styled.div`
  min-width: 100px;
`;

const CompactDropdownMenu = styled.div`
  .dropdown-item span {
    font-size: 11px;
  }
`;

const LogoutItem = styled.div`
  padding: 5px 8px;
  cursor: pointer;
  span {
    font-size: 11px;
  }
`;

const WhisperTarget = styled.div`
  display: flex;
  align-items: center;
`;

interface ToggleProps {
  onClick?: (e: React.MouseEvent) => void;
}
const Toggle = React.forwardRef<HTMLDivElement, ToggleProps>(({ onClick }, ref) => {
  return (
    <IconBox
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick && onClick(e);
      }}>
      <Icon.More width={14.4} height={14.4} />
    </IconBox>
  );
});

interface ProfileMenuEntry {
  key: string;
  label: string;
  path: string;
  visible: boolean;
}

function getProfileMenuItems(): ProfileMenuEntry[] {
  return [
    { key: 'message-template', label: i18n.t('gnb.profile.메시지 템플릿'), path: PATH.MESSAGE_TEMPLATE, visible: true },
    { key: 'user', label: '사용자', path: PATH.USER, visible: true },
    { key: 'settings', label: i18n.t('gnb.profile.설정'), path: PATH.SETTINGS, visible: MainSetting.showSetting },
    {
      key: 'billing',
      label: i18n.t('gnb.profile.결제'),
      path: PATH.BILLING_OVERVIEW,
      visible: MainSetting.showPaymentManagement,
    },
  ].filter((item) => item.visible);
}

interface ProfileItemProps {
  collapsed?: boolean;
}

const ProfileItem: React.FC<ProfileItemProps> = ({ collapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile, logout } = useAuth();
  const showLabel = useDelayedVisibility(collapsed);
  const menuItems = getProfileMenuItems();
  const active = menuItems.some((item) => location.pathname.includes(item.path));

  const menuPopover = (
    <Popover>
      <PopoverMenu>
        {menuItems.map((item) => (
          <SubMenuItem key={item.key} title={item.label} href={item.path} inPopover />
        ))}
        <LogoutItem onClick={() => logout(true)}>
          <Text color="red50" typo="body3">
            {i18n.t('gnb.profile.로그아웃')}
          </Text>
        </LogoutItem>
      </PopoverMenu>
    </Popover>
  );

  const profileContent = (
    <ProfileBox $active={active}>
      <Icon.User width={24} height={24} />
      {showLabel && (
        <>
          <TextBox>
            <Text typo="body5" color="grayBlack">
              {profile?.name}
            </Text>
          </TextBox>

          <CompactDropdownMenu>
            <Dropdown id="profile-dropdown" align="start" drop="up-centered">
              <DropdownToggle as={Toggle} />
              <DropdownMenu positioningStrategy="fixed">
                {menuItems.map((item) => (
                  <DropdownItem key={item.key} onClick={() => navigate(item.path)}>
                    {item.label}
                  </DropdownItem>
                ))}
                <DropdownItem
                  danger
                  onClick={() => {
                    logout(true);
                  }}>
                  {i18n.t('gnb.profile.로그아웃')}
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </CompactDropdownMenu>
        </>
      )}
    </ProfileBox>
  );

  if (collapsed) {
    return (
      <ProfileBox $active={active}>
        <Whisper placement="rightEnd" trigger="hover" speaker={menuPopover} enterable>
          <WhisperTarget>
            <Icon.User width={24} height={24} />
          </WhisperTarget>
        </Whisper>
      </ProfileBox>
    );
  }

  return profileContent;
};

export default ProfileItem;
